import axios from 'axios';
import { useSparrowProductionUrl, timeoutInMinutes } from '../config/sparrow';
import { useRef, useCallback, useEffect } from 'react';

const usePostSparrowChat = () => {
  const controllerRef = useRef(null);

  const postChat = useCallback(async ({ message, chatId, contextMemory = '', file = null }) => {
    // Abort any in-flight request
    controllerRef.current?.abort();

    // Create a new controller for this request
    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    const timeoutMilliseconds = timeoutInMinutes*60*1000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMilliseconds);

    // Build form data
    const formData = new FormData();
    formData.append('message', message);
    formData.append('ChatId', chatId);
    formData.append('ContextMemory', contextMemory);
    if (file) {
      formData.append('file', file);
    }

    try {
      // Send request with cancellation support
      const { data } = await axios.post(
        `/api/Sparrow/sparrow/chat?useProduction=${useSparrowProductionUrl}`,
        formData,
        { signal }
      );

      return {
        response: data?.response || '',
        contextMemory: data?.context_memory || ''
      };
    } catch (err) {
      // Handle cancelation or timeout
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.warn('Sparrow request cancelled or timed out');
        throw new Error('Request cancelled or timed out');
      }
      // Rethrow other errors
      console.error('Error posting to Sparrow chat endpoint:', err.response?.data || err.message);
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  // Expose a cancel function if you need to trigger it manually
  const cancel = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  //If any component using this hook unmounts (navigates away, cancellation token is called on request to api)
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  return { postChat, cancel };
};

export default usePostSparrowChat;
